// pages/Profile.tsx
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Edit, LogOut, User, Wallet, Settings, Award, Bell, ChevronRight } from "lucide-react";
import { useDarkMode } from '../context/DarkModeContext';
import EditProfileModal from '../components/EditProfileModal';
import { DEFAULT_USER_PROFILE, ACHIEVEMENTS } from '../lib/constants/profile';
import type { UserProfile } from '../lib/constants/profile';
import Footer from '../components/Footer';

const Profile = () => {
  const { darkMode } = useDarkMode();
  const [showSettings, setShowSettings] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile>(() => {
    // Load saved profile from localStorage on initial render
    const savedProfile = localStorage.getItem('userProfile');
    if (savedProfile) {
      return JSON.parse(savedProfile);
    }
    return {
      ...DEFAULT_USER_PROFILE,
      avatar: "/michael-coffie.jpg"
    };
  });

  // Save profile to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('userProfile', JSON.stringify(userProfile));
  }, [userProfile]);

  const handleProfileUpdate = (updatedProfile: UserProfile) => {
    try {
      // Update the profile state
      setUserProfile(updatedProfile);
      
      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successMessage.textContent = 'Profile updated successfully!';
      document.body.appendChild(successMessage);
      
      // Remove the success message after 3 seconds
      setTimeout(() => {
        document.body.removeChild(successMessage);
      }, 3000);
      
      setShowEditProfile(false);
    } catch (error) {
      // Show error message
      const errorMessage = document.createElement('div');
      errorMessage.className = 'fixed bottom-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      errorMessage.textContent = 'Failed to update profile. Please try again.';
      document.body.appendChild(errorMessage);
      
      // Remove the error message after 3 seconds
      setTimeout(() => {
        document.body.removeChild(errorMessage);
      }, 3000);
      
      console.error('Error updating profile:', error);
    }
  };

  // Function to handle logout
  const handleLogout = () => {
    // Clear stored profile
    localStorage.removeItem('userProfile');
    // Reset to default profile
    setUserProfile({
      ...DEFAULT_USER_PROFILE,
      avatar: "/michael-coffie.jpg"
    });
    // You can add additional logout logic here (e.g., redirect to login page)
  };

  // Dummy data - replace with real data later
  const userData = {
    name: "Michael Coffie",
    role: "AI Finance Pioneer 🚀",
    avatar: "/michael-coffie.jpg",
    balance: 5200,
    transactions: 15,
    aiScore: 92,
    achievements: [
      { title: "Early Adopter", description: "Joined Green Guard's first wave", icon: "🌟" },
      { title: "Smart Saver", description: "Saved 20% more than last month", icon: "💰" },
      { title: "AI Explorer", description: "Used all AI features", icon: "🤖" }
    ],
    insights: [
      "You've reduced unnecessary spending by 15% this month",
      "Your savings rate is higher than 80% of users",
      "Consider investing in tech stocks based on your risk profile"
    ]
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-16 px-4 pb-32">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Hero Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl p-8 shadow-xl"
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative group"
              >
                <div className="w-32 h-32 rounded-full overflow-hidden ring-4 ring-green-500/20">
                  <img 
                    src={userProfile.avatar} 
                    alt={userProfile.name}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition duration-500"
                  />
                </div>
                <motion.div 
                  className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                >
                  <Edit className="w-6 h-6 text-white" />
                </motion.div>
              </motion.div>
              
              <div className="text-center md:text-left">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {userProfile.name}
                </h1>
                <p className="text-gray-600 dark:text-gray-300 mt-2">
                  {userProfile.role}
                </p>
                <div className="flex gap-4 mt-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowEditProfile(true)}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Edit Profile
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition flex items-center gap-2"
                    onClick={() => setShowSettings(true)}
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl p-6 shadow-lg"
            >
              <Wallet className="w-8 h-8 text-green-500 mb-4" />
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ${userData.balance}
              </p>
              <p className="text-gray-600 dark:text-gray-300">Current Balance</p>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl p-6 shadow-lg"
            >
              <Bell className="w-8 h-8 text-blue-500 mb-4" />
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {userData.transactions}
              </p>
              <p className="text-gray-600 dark:text-gray-300">Recent Transactions</p>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl p-6 shadow-lg"
            >
              <Award className="w-8 h-8 text-yellow-500 mb-4" />
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {userData.aiScore}%
              </p>
              <p className="text-gray-600 dark:text-gray-300">AI Trust Score</p>
            </motion.div>
          </div>

          {/* Achievements Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl p-8 shadow-xl"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Achievements
            </h2>
            <div className="space-y-4">
              {userData.achievements.map((achievement, index) => (
                <motion.div
                  key={achievement.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                >
                  <div className="text-3xl">{achievement.icon}</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {achievement.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      {achievement.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* AI Insights */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-green-500/10 to-blue-500/10 backdrop-blur-xl rounded-3xl p-8 shadow-xl"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <Settings className="w-6 h-6" />
              AI Insights
            </h2>
            <div className="space-y-4">
              {userData.insights.map((insight, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-4 bg-white/40 dark:bg-gray-800/40 rounded-xl"
                >
                  <div className="text-green-500">💡</div>
                  <p className="text-gray-700 dark:text-gray-200">{insight}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-3xl p-8 max-w-lg w-full shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Settings
              </h2>
              {/* Add settings options here */}
              <button
                onClick={() => setShowSettings(false)}
                className="mt-6 w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add the EditProfileModal */}
      <AnimatePresence>
        {showEditProfile && (
          <EditProfileModal
            isOpen={showEditProfile}
            onClose={() => setShowEditProfile(false)}
            userProfile={userProfile}
            onSave={handleProfileUpdate}
          />
        )}
      </AnimatePresence>
      
      <Footer />
    </>
  );
};

export default Profile;